import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Auth, Offers, Restaurant } from "../lib/api";
import { LogOut, Plus, Trash2, Save, ExternalLink } from "lucide-react";
import { toast, Toaster } from "sonner";

function emptyOffer() {
  return {
    title: "",
    description: "",
    price: "",
    valid_until: "",
    is_active: true,
  };
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [tab, setTab] = useState("offers");
  const [loading, setLoading] = useState(!location.state?.user);

  useEffect(() => {
    if (location.state?.user) return;
    Auth.me()
      .then(setUser)
      .catch(() => navigate("/admin/login", { replace: true }))
      .finally(() => setLoading(false));
  }, [location, navigate]);

  const handleLogout = async () => {
    await Auth.logout();
    navigate("/admin/login", { replace: true });
  };

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center font-display text-2xl">
        Loading…
      </div>
    );
  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-black">
      <Toaster position="top-right" richColors />
      <header className="border-b-2 border-black bg-white sticky top-0 z-30">
        <div className="mx-auto max-w-6xl px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="font-display text-2xl">KKFC Admin</div>
            <span className="hidden sm:inline text-xs uppercase tracking-widest brutal-border bg-[#FFB800] px-2 py-0.5">
              Bangarpet
            </span>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              className="hidden sm:inline-flex items-center gap-1 text-xs uppercase tracking-widest font-semibold hover:text-[#D92525]"
            >
              <ExternalLink size={14} /> View Site
            </a>
            <span className="text-xs text-neutral-700 hidden md:inline">
              {user.email}
            </span>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="brutal-border bg-black text-white px-3 py-1.5 text-xs font-bold uppercase tracking-widest inline-flex items-center gap-1.5"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
        <nav className="mx-auto max-w-6xl px-5 md:px-8 flex gap-2 pb-3">
          {[
            { id: "offers", label: "Daily Offers" },
            { id: "info", label: "Restaurant Info" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`px-4 py-2 text-sm font-bold uppercase tracking-wide brutal-border ${
                tab === t.id
                  ? "bg-[#D92525] text-white"
                  : "bg-white text-black"
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </header>

      <main className="mx-auto max-w-6xl px-5 md:px-8 py-10">
        {tab === "offers" && <OffersTab />}
        {tab === "info" && <InfoTab />}
      </main>
    </div>
  );
}

function OffersTab() {
  const [offers, setOffers] = useState([]);
  const [draft, setDraft] = useState(emptyOffer());
  const [loading, setLoading] = useState(true);

  const load = () =>
    Offers.list()
      .then(setOffers)
      .finally(() => setLoading(false));
  useEffect(() => {
    load();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...draft,
        price: draft.price === "" ? null : Number(draft.price),
        valid_until: draft.valid_until || null,
      };
      await Offers.create(payload);
      toast.success("Offer added!");
      setDraft(emptyOffer());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete offer?")) return;
    await Offers.remove(id);
    toast.success("Deleted");
    load();
  };

  const toggleActive = async (o) => {
    await Offers.update(o.id, { ...o, is_active: !o.is_active });
    load();
  };

  return (
    <div className="grid lg:grid-cols-12 gap-6">
      <div className="lg:col-span-5">
        <div className="brutal-border brutal-shadow bg-white p-6">
          <h2 className="font-display text-3xl uppercase mb-4">
            <Plus className="inline mr-2" /> New Offer
          </h2>
          <form onSubmit={handleCreate} className="space-y-3">
            <Field label="Title" required>
              <input
                value={draft.title}
                onChange={(e) =>
                  setDraft({ ...draft, title: e.target.value })
                }
                data-testid="offer-title-input"
                required
                placeholder="e.g. Family Bucket Combo"
                className="w-full brutal-border bg-white px-3 py-2 text-sm"
              />
            </Field>
            <Field label="Description" required>
              <textarea
                value={draft.description}
                onChange={(e) =>
                  setDraft({ ...draft, description: e.target.value })
                }
                data-testid="offer-desc-input"
                required
                rows={2}
                placeholder="8 pcs bucket + 2 burgers + 4 cokes"
                className="w-full brutal-border bg-white px-3 py-2 text-sm resize-none"
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Price (₹)">
                <input
                  type="number"
                  value={draft.price}
                  onChange={(e) =>
                    setDraft({ ...draft, price: e.target.value })
                  }
                  data-testid="offer-price-input"
                  placeholder="490"
                  className="w-full brutal-border bg-white px-3 py-2 text-sm"
                />
              </Field>
              <Field label="Valid Until">
                <input
                  type="date"
                  value={draft.valid_until}
                  onChange={(e) =>
                    setDraft({ ...draft, valid_until: e.target.value })
                  }
                  data-testid="offer-valid-input"
                  className="w-full brutal-border bg-white px-3 py-2 text-sm"
                />
              </Field>
            </div>
            <button
              type="submit"
              data-testid="offer-submit-btn"
              className="w-full brutal-border brutal-shadow bg-[#D92525] text-white font-bold px-4 py-3 text-sm uppercase tracking-widest"
            >
              Save Offer
            </button>
          </form>
        </div>
      </div>

      <div className="lg:col-span-7">
        <h2 className="font-display text-3xl uppercase mb-4">
          Active Offers ({offers.length})
        </h2>
        {loading && <div>Loading…</div>}
        {!loading && offers.length === 0 && (
          <div className="brutal-border bg-white p-6 text-center text-neutral-600">
            No offers yet — add one using the form on the left.
          </div>
        )}
        <div className="space-y-3">
          {offers.map((o) => (
            <div
              key={o.id}
              data-testid={`offer-row-${o.id}`}
              className="brutal-border brutal-shadow bg-white p-4 flex items-start gap-3"
            >
              <div className="flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <div className="font-display text-2xl">{o.title}</div>
                  {o.price != null && (
                    <div className="font-display text-2xl text-[#D92525]">
                      ₹{o.price}
                    </div>
                  )}
                </div>
                <div className="text-sm text-neutral-700 mt-1">
                  {o.description}
                </div>
                <div className="flex flex-wrap gap-2 mt-2 text-[11px] uppercase tracking-widest">
                  {o.valid_until && (
                    <span className="brutal-border bg-[#FFB800] px-2 py-0.5">
                      Till {o.valid_until}
                    </span>
                  )}
                  <button
                    onClick={() => toggleActive(o)}
                    className={`brutal-border px-2 py-0.5 ${o.is_active ? "bg-[#25D366] text-black" : "bg-neutral-200"}`}
                  >
                    {o.is_active ? "Active" : "Hidden"}
                  </button>
                </div>
              </div>
              <button
                onClick={() => handleDelete(o.id)}
                className="brutal-border bg-black text-white p-2"
                data-testid={`delete-offer-${o.id}`}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function InfoTab() {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    Restaurant.get().then(setInfo);
  }, []);
  if (!info) return <div>Loading…</div>;

  const save = async (e) => {
    e.preventDefault();
    try {
      await Restaurant.update(info);
      toast.success("Restaurant info updated!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const setHour = (i, k, v) => {
    const h = [...info.hours];
    h[i] = { ...h[i], [k]: v };
    setInfo({ ...info, hours: h });
  };

  return (
    <form onSubmit={save} className="brutal-border brutal-shadow bg-white p-6 max-w-3xl">
      <h2 className="font-display text-3xl uppercase mb-5">Restaurant Info</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Display Phone">
          <input
            value={info.phone}
            onChange={(e) => setInfo({ ...info, phone: e.target.value })}
            className="w-full brutal-border px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Phone (tel:)">
          <input
            value={info.phone_tel}
            onChange={(e) => setInfo({ ...info, phone_tel: e.target.value })}
            className="w-full brutal-border px-3 py-2 text-sm"
          />
        </Field>
        <Field label="WhatsApp Number (no +)">
          <input
            value={info.wa_number}
            onChange={(e) => setInfo({ ...info, wa_number: e.target.value })}
            className="w-full brutal-border px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Restaurant Name">
          <input
            value={info.name}
            onChange={(e) => setInfo({ ...info, name: e.target.value })}
            className="w-full brutal-border px-3 py-2 text-sm"
          />
        </Field>
      </div>
      <Field label="Address" className="mt-4">
        <textarea
          rows={2}
          value={info.address}
          onChange={(e) => setInfo({ ...info, address: e.target.value })}
          className="w-full brutal-border px-3 py-2 text-sm resize-none"
        />
      </Field>
      <Field label="Map Embed URL" className="mt-4">
        <input
          value={info.map_embed}
          onChange={(e) => setInfo({ ...info, map_embed: e.target.value })}
          className="w-full brutal-border px-3 py-2 text-sm"
        />
      </Field>
      <div className="mt-5">
        <div className="text-xs uppercase tracking-widest font-bold mb-2">
          Hours
        </div>
        {info.hours.map((h, i) => (
          <div key={i} className="grid grid-cols-2 gap-3 mb-2">
            <input
              value={h.day}
              onChange={(e) => setHour(i, "day", e.target.value)}
              className="brutal-border px-3 py-2 text-sm"
            />
            <input
              value={h.time}
              onChange={(e) => setHour(i, "time", e.target.value)}
              className="brutal-border px-3 py-2 text-sm"
            />
          </div>
        ))}
      </div>
      <button
        type="submit"
        data-testid="save-info-btn"
        className="mt-5 brutal-border brutal-shadow bg-[#D92525] text-white font-bold px-5 py-3 text-sm uppercase tracking-widest inline-flex items-center gap-2"
      >
        <Save size={16} /> Save Changes
      </button>
    </form>
  );
}

function Field({ label, children, required, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[11px] uppercase tracking-widest font-bold text-neutral-700">
        {label} {required && <span className="text-[#D92525]">*</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
